import React from 'react';
import { useDraggable } from '@dnd-kit/core';
import { Task } from '../../types/roadmap';
import { GripVertical, Edit2, Trash2 } from 'lucide-react';
import { getTimePeriodConfig } from '../../utils/timePeriods';

interface TaskCardProps {
  task: Task;
  isDragging?: boolean;
  onEdit?: (task: Task) => void;
  onDelete?: (taskId: string) => void;
}

export const TaskCard: React.FC<TaskCardProps> = ({ task, isDragging, onEdit, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: task.id,
  });

  const style = transform ? {
    transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
  } : undefined;

  const periodConfig = getTimePeriodConfig(task.time_period);
  const bgColorClass = `${periodConfig.color.bg} ${periodConfig.color.border} hover:bg-opacity-80`;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`${bgColorClass} border-l-4 rounded p-2 text-xs group cursor-grab active:cursor-grabbing transition-all ${
        isDragging ? 'opacity-50 shadow-xl scale-105' : 'shadow-sm'
      }`}
    >
      <div className="flex items-start gap-2">
        <div {...listeners} {...attributes} className="cursor-grab active:cursor-grabbing pt-0.5 flex-shrink-0">
          <GripVertical size={14} className="text-gray-400" />
        </div>

        <div className="flex-1 min-w-0">
          {task.title}
        </div>

        {!isDragging && (onEdit || onDelete) && (
          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
            {onEdit && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(task);
                }}
                className="p-1 hover:bg-white rounded transition-colors"
                title="Edit task"
              >
                <Edit2 size={12} className="text-blue-600" />
              </button>
            )}
            {onDelete && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(task.id);
                }}
                className="p-1 hover:bg-white rounded transition-colors"
                title="Delete task"
              >
                <Trash2 size={12} className="text-red-600" />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
